import { Typography, TypographyProps } from '@mui/material';
import React, { Children, isValidElement } from 'react';
import MDImg from './MDImg';

const isBlank = (child: React.ReactNode) =>
	typeof child === 'string' && child.trim() === '';

/**
 * Markdown wraps a lone image in a paragraph. Left wrapped, the image is a
 * descendant of the stacked wrapper rather than a direct child, so it misses
 * the "the container already spaces me" check and adds its own top margin on
 * top of the container's gap. Unwrapping it keeps the image a direct child,
 * spaced only by that gap - while an image that follows text inside a list
 * item stays wrapped and keeps its own margin, which is what spaces it from
 * the text above.
 */
const MDParagraph: React.FC<TypographyProps> = ({
	children = null,
	...paragraphProps
}) => {
	const meaningful = Children.toArray(children).filter(
		(child) => !isBlank(child)
	);

	// Parsed <p> attributes live on the wrapper, so a paragraph carrying any of
	// them keeps it: dropping or unwrapping would lose the attribute along with
	// the element that held it. Checked by value, not by key - markdown always
	// passes className, undefined when the paragraph has none.
	const hasAttributes = Object.values(paragraphProps).some(
		(value) => value !== undefined && value !== null && value !== ''
	);

	// A line holding only whitespace or an &nbsp; is not a paragraph anyone
	// meant to write, but it still takes a line box plus a gap on either side -
	// which reads as a hole in the page.
	if (meaningful.length === 0 && !hasAttributes) {
		return null;
	}

	const [only] = meaningful;

	if (
		!hasAttributes &&
		meaningful.length === 1 &&
		isValidElement(only) &&
		only.type === MDImg
	) {
		return only;
	}

	return (
		<Typography variant="body1" component="div" {...paragraphProps}>
			{children}
		</Typography>
	);
};

export default MDParagraph;
